"use client";

import { useEffect } from "react";
import type { ShellRenderBoundary } from "@smartmanager/shell-ui/host";

import { recordClientOperationalEvent } from "@/src/client/observability/axiomClient";
import type { RenderBoundarySource } from "@/src/observability/clientContract";

/**
 * Next.js hands an error boundary a server render failure as a generic
 * `Error` whose `digest` is a hash of the server-side message and stack. The
 * digest is message-derived, so it never leaves the device; its presence only
 * classifies the failure as server-originated (the server already reported it
 * through `onRequestError`).
 */
const SERVER_RENDER_ERROR_TYPE = "ServerRenderError";

// One report per caught error object: React re-runs effects in development
// Strict Mode, and a boundary can re-render the same failure.
const reported = new WeakSet<object>();

function renderErrorType(error: unknown): string {
  if (!error || typeof error !== "object") return typeof error === "string" ? "ThrownString" : "NonErrorThrown";
  const { digest, name } = error as { digest?: unknown; name?: unknown };
  if (typeof digest === "string" && digest.length > 0) return SERVER_RENDER_ERROR_TYPE;
  return typeof name === "string" && name.length > 0 ? name : "Error";
}

/**
 * Reports a render failure an error boundary caught. React does not rethrow
 * what a boundary catches, so the window `error` listener never sees it.
 *
 * The event carries the boundary, the error's type and the route pattern
 * only: never the message, stack, component stack or digest. Recording is a
 * no-op until diagnostics consent starts client observability.
 */
export function reportRenderBoundaryError(source: RenderBoundarySource, error: unknown): void {
  if (error && typeof error === "object") {
    if (reported.has(error)) return;
    reported.add(error);
  }
  recordClientOperationalEvent({
    event_name: "browser.render.error",
    severity: "error",
    error_type: renderErrorType(error),
    source,
    route: typeof window === "undefined" ? null : window.location.pathname,
  });
}

/** The app hosts' `reportRenderError` port: a shell load boundary's failure. */
export function reportShellRenderError(boundary: ShellRenderBoundary, error: unknown): void {
  reportRenderBoundaryError(`${boundary}-load-boundary`, error);
}

/** Reports the boundary's error once, after the fallback commits. */
export function useReportRenderBoundaryError(source: RenderBoundarySource, error: unknown): void {
  useEffect(() => {
    reportRenderBoundaryError(source, error);
  }, [source, error]);
}
